import type { ProviderDetail } from '../../../../../js/api';
import { Badge } from '../../../components/badge';
import { ExpirationDate } from './LicensesTab';
import {
  fileCountFor,
  formatFileCount,
  formatProviderDate,
  labelFor,
  ProviderDrawerMaps,
  textOrDash,
  yesNo,
} from './providerDisplay';
import {
  FieldSection,
  FilePresenceIcon,
  RecordCard,
  RecordSection,
} from './ProviderDrawerPrimitives';

/** `ProviderFileType` code of the malpractice insurance certificates (files only, so a count). */
export const INSURANCE_FILE_TYPE = 'COI';

/** Insurance count, other documents, questionnaire flag and immunizations — the old "Other" tab. */
export function OtherTab({
  provider,
  maps,
}: {
  provider: ProviderDetail;
  maps: ProviderDrawerMaps;
}) {
  return (
    <div className="tw:flex tw:flex-col tw:gap-4">
      <div className="tw:grid tw:grid-cols-1 tw:md:grid-cols-2 tw:gap-x-3 tw:gap-y-4">
        <FieldSection
          title="Insurance Certificates"
          fields={[
            {
              label: maps.ProviderFileType?.[INSURANCE_FILE_TYPE] || 'Insurance certificates',
              value: formatFileCount(fileCountFor(provider.file_counts, INSURANCE_FILE_TYPE)),
            },
          ]}
          data-testid="provider-insurance"
        />
        <FieldSection
          title="Questionnaire"
          fields={[{ label: 'Completed', value: yesNo(provider.completed_questionnaire) }]}
          data-testid="provider-questionnaire"
        />
      </div>
      <RecordSection
        title="Other Documents"
        count={provider.miscellaneous.length}
        emptyText="No other documents registered."
        data-testid="provider-miscellaneous"
      >
        {provider.miscellaneous.map((record, index) => (
          <RecordCard
            key={record.id}
            title={`Record #${index + 1}`}
            rightElement={<FilePresenceIcon hasFile={record.has_file} />}
            fields={[
              { label: 'Type', value: textOrDash(record.title) },
              { label: 'Issued', value: formatProviderDate(record.issue_date) },
              { label: 'Expires', value: <ExpirationDate value={record.expiration_date} /> },
            ]}
          />
        ))}
      </RecordSection>
      <RecordSection
        title="Immunizations"
        count={provider.immunizations.length}
        emptyText="No immunization certificates registered."
        data-testid="provider-immunizations"
      >
        {provider.immunizations.map((immunization) => (
          <RecordCard
            key={immunization.id}
            title={labelFor(immunization.type, maps.ImmunizationType)}
            rightElement={
              <span className="tw:flex tw:items-center tw:gap-2">
                <FilePresenceIcon hasFile={immunization.has_file} />
                {immunization.exempt && <Badge color="amber">Exempt</Badge>}
              </span>
            }
            fields={[
              { label: 'Issued', value: formatProviderDate(immunization.issue_date) },
              { label: 'Expires', value: <ExpirationDate value={immunization.expiration_date} /> },
              { label: 'Exempt', value: yesNo(immunization.exempt) },
            ]}
          />
        ))}
      </RecordSection>
    </div>
  );
}
